import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SettlementEventName } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { startCase } from 'lodash-es';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Settlement, SettlementEvent } from '~hooks/useSettlements/models';
import useSettlementEventsInfiniteQuery from '~queries/useSettlementEventsInfiniteQuery';
import { dateFormat } from '~utils/dateTime';
import isEnumValue from '~utils/isEnumValue';

import { settlementSheetCardSx } from './settlementSheetDisplay';

const HISTORY_EVENT_DATE_TIME_FORMAT = 'MMM D, YYYY h:mm A';

const labelKeyBySettlementEventName = {
  [SettlementEventName.SETTLEMENT_APPROVED]: 'status.approved',
  [SettlementEventName.SETTLEMENT_CUSTOMER_APPROVED]: 'status.approved',
  [SettlementEventName.SETTLEMENT_CUSTOMER_PAID]: 'status.paid',
  [SettlementEventName.SETTLEMENT_EXPORTED]: 'status.exported',
  [SettlementEventName.SETTLEMENT_LINE_ADDED]:
    'approvals.driver_pay.settlements.sheet.history.line_added',
  [SettlementEventName.SETTLEMENT_LINE_EDITED]:
    'approvals.driver_pay.settlements.sheet.history.line_edited',
  [SettlementEventName.SETTLEMENT_LINE_REMOVED]:
    'approvals.driver_pay.settlements.sheet.history.line_removed',
  [SettlementEventName.SETTLEMENT_PAID]: 'status.paid',
  [SettlementEventName.SETTLEMENT_PAYMENT_UNDONE]:
    'approvals.driver_pay.settlements.sheet.history.payment_undone',
  [SettlementEventName.SETTLEMENT_SENT]: 'status.sent',
  [SettlementEventName.SETTLEMENT_UNAPPROVED]:
    'approvals.driver_pay.settlements.sheet.history.unapproved',
  [SettlementEventName.SETTLEMENT_VOIDED]:
    'approvals.driver_pay.settlements.sheet.history.voided',
} satisfies Record<SettlementEventName, string>;

const getEventLabel = (event: SettlementEvent) =>
  isEnumValue(SettlementEventName, event.eventName)
    ? t(labelKeyBySettlementEventName[event.eventName])
    : startCase(event.eventName);

interface SettlementSheetHistoryProps {
  companyId: string;
  settlement: Settlement;
}

export default function SettlementSheetHistory({
  companyId,
  settlement,
}: SettlementSheetHistoryProps) {
  const { events, fetchMore, hasMore, isError, isLoading, isLoadingMore, refetch } =
    useSettlementEventsInfiniteQuery({
      companyId,
      settlementId: settlement.id ?? '',
      settlementUpdatedAt: settlement.updatedAt.toISOString(),
    });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasFirstLoadFailed = isError && events.length === 0;

  if (hasFirstLoadFailed) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 3,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('error_messages.settlements.failed_to_fetch_history')}
        </Typography>
        <SmallButton color="secondary" onClick={() => void refetch()} variant="outlined">
          {t('actions.retry')}
        </SmallButton>
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', p: 3, textAlign: 'center' }}
      >
        {t('approvals.driver_pay.settlements.sheet.history.empty')}
      </Typography>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        ...settlementSheetCardSx(theme),
        alignItems: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      })}
    >
      <Stack divider={<Divider />} spacing={1} sx={{ width: '100%' }}>
        {events.map((event) => {
          const voidReasonToShow =
            event.eventName === SettlementEventName.SETTLEMENT_VOIDED
              ? settlement.voidReason
              : null;

          return (
            <Box
              key={event.id}
              data-test-id="settlement-sheet-history-event"
              sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {getEventLabel(event)}
                </Typography>
                {event.createdByName && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {event.createdByName}
                  </Typography>
                )}
                {voidReasonToShow && (
                  <Typography variant="body2">
                    {t('approvals.driver_pay.settlements.sheet.history.void_reason', {
                      interpolation: { escapeValue: false },
                      reason: voidReasonToShow,
                    })}
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                {dateFormat(event.occurredAt, HISTORY_EVENT_DATE_TIME_FORMAT)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      {isError && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('error_messages.settlements.failed_to_fetch_history')}
        </Typography>
      )}
      {hasMore && (
        <SmallLoadingButton
          color="secondary"
          loading={isLoadingMore}
          onClick={() => void fetchMore()}
          variant="outlined"
        >
          {t('actions.show_more')}
        </SmallLoadingButton>
      )}
    </Box>
  );
}
